import {
  type AdminStructure,
  type AdminStructuresResponse,
  PROJECT_STATUSES,
  PROJECT_STATUS_LABELS,
  type ProjectAdminItem,
  type ProjectStatus,
  STRUCTURE_KINDS,
  STRUCTURE_KIND_LABELS,
  type StructureKind,
  adminStructuresResponseSchema,
  inspectionTemplateResponseSchema,
  projectAdminItemSchema,
  projectAdminListResponseSchema,
} from '@sivan/shared';
import { askConfirm } from '../ui/confirm';
import { useId, useState } from 'react';
import { apiGet, apiPost, apiPut } from '../api/client';
import { LoadingBlock, Problem } from '../handover/common';
import { ProgressBar } from '../handover/InspectionPanel';
import { ProjectTeam } from '../team/TeamScreen';
import { HandoverSettings } from './HandoverSettings';
import { Modal } from './Modal';
import {
  type SetupStepId,
  explainProjectError,
  readinessChecks,
  setupSteps,
} from './project-setup';
import { Icon } from '../ui/Icon';
import { useLoad } from './use-master-data';

// "ניהול פרויקטים" in Master Data (PROJECTS-1): the projects the caller may manage, a new project,
// and the guided setup of one project. Synthetic development data only.

type Notice = { readonly kind: 'error' | 'done'; readonly text: string } | null;

function NoticeLine({ notice }: { readonly notice: Notice }) {
  if (notice === null) return null;
  return (
    <p
      className={`notice ${notice.kind === 'error' ? 'notice--error' : 'notice--success'}`}
      role={notice.kind === 'error' ? 'alert' : 'status'}
    >
      {notice.text}
    </p>
  );
}

interface DetailsValues {
  name: string;
  code: string;
  city: string;
  address: string;
  description: string;
  status: ProjectStatus;
}

function DetailsForm({
  initial,
  creating,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  readonly initial: DetailsValues;
  readonly creating: boolean;
  readonly submitLabel: string;
  readonly onSubmit: (values: DetailsValues) => Promise<void>;
  readonly onCancel?: () => void;
}) {
  const [values, setValues] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);
  const set = (change: Partial<DetailsValues>) => {
    setValues((v) => ({ ...v, ...change }));
  };
  return (
    <form
      className="project-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (values.name.trim().length < 2 || values.city.trim().length < 2) {
          setNotice({ kind: 'error', text: 'יש להזין שם פרויקט ועיר.' });
          return;
        }
        setBusy(true);
        setNotice(null);
        onSubmit(values).then(
          () => {
            setBusy(false);
            if (!creating) setNotice({ kind: 'done', text: 'פרטי הפרויקט נשמרו.' });
          },
          (error: unknown) => {
            setBusy(false);
            setNotice({ kind: 'error', text: explainProjectError(error) });
          },
        );
      }}
    >
      <label className="field">
        <span>שם הפרויקט</span>
        <input
          value={values.name}
          maxLength={80}
          onChange={(e) => {
            set({ name: e.target.value });
          }}
        />
      </label>
      <label className="field">
        <span>{creating ? 'קוד פרויקט (רשות)' : 'קוד פרויקט'}</span>
        <input
          dir="ltr"
          value={values.code}
          maxLength={12}
          placeholder={creating ? 'ייווצר אוטומטית' : undefined}
          onChange={(e) => {
            set({ code: e.target.value.toUpperCase() });
          }}
        />
        <span className="hint">אותיות לועזיות גדולות, ספרות ומקף.</span>
      </label>
      <div className="project-form__pair">
        <label className="field">
          <span>עיר</span>
          <input
            value={values.city}
            maxLength={80}
            onChange={(e) => {
              set({ city: e.target.value });
            }}
          />
        </label>
        <label className="field">
          <span>כתובת (רשות)</span>
          <input
            value={values.address}
            maxLength={200}
            onChange={(e) => {
              set({ address: e.target.value });
            }}
          />
        </label>
      </div>
      <label className="field">
        <span>תיאור (רשות)</span>
        <textarea
          rows={3}
          maxLength={2000}
          value={values.description}
          onChange={(e) => {
            set({ description: e.target.value });
          }}
        />
      </label>
      {!creating && (
        <label className="field">
          <span>סטטוס</span>
          <select
            value={values.status}
            onChange={(e) => {
              set({ status: e.target.value as ProjectStatus });
            }}
          >
            {PROJECT_STATUSES.map((s) => (
              <option key={s} value={s}>
                {PROJECT_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        </label>
      )}
      <NoticeLine notice={notice} />
      <div className="form-actions">
        <button type="submit" className="button button--primary" disabled={busy}>
          {busy ? 'שומר…' : submitLabel}
        </button>
        {onCancel !== undefined && (
          <button type="button" className="button" onClick={onCancel}>
            ביטול
          </button>
        )}
      </div>
    </form>
  );
}

function BuildingCard({
  projectId,
  building,
  editable,
  onChanged,
}: {
  readonly projectId: string;
  readonly building: AdminStructure;
  readonly editable: boolean;
  readonly onChanged: (data: AdminStructuresResponse, text: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [values, setValues] = useState({
    code: building.code,
    name: building.name,
    kind: building.kind,
  });
  const [range, setRange] = useState({ from: '0', to: '' });
  const [level, setLevel] = useState<{
    id: string;
    number: string;
    label: string;
    rowVersion: number;
  } | null>(null);
  const [notice, setNotice] = useState<Notice>(null);
  const run = async (action: () => Promise<AdminStructuresResponse>, text: string) => {
    setNotice(null);
    try {
      onChanged(await action(), text);
    } catch (error) {
      setNotice({ kind: 'error', text: explainProjectError(error) });
    }
  };
  const base = `/project-admin/${projectId}`;
  const save = (status: 'active' | 'archived') =>
    run(
      () =>
        apiPut(
          `${base}/structures/${building.id}`,
          { rowVersion: building.rowVersion, ...values, status },
          adminStructuresResponseSchema,
        ),
      status === 'archived' ? 'הבניין הועבר לארכיון.' : 'פרטי הבניין נשמרו.',
    );
  return (
    <li
      className={`building${building.status === 'archived' ? ' building--archived' : ''}`}
      aria-label={`${building.name} (${building.code})`}
    >
      <div className="building__head">
        <p className="building__name">
          {building.name}{' '}
          <span className="hint">
            (<bdi dir="ltr">{building.code}</bdi>)
          </span>
        </p>
        <span className="chip">{STRUCTURE_KIND_LABELS[building.kind]}</span>
        {building.status === 'archived' && <span className="chip">בארכיון</span>}
        <span className="hint">
          {building.levels.length} קומות · {building.units} יחידות
        </span>
        {editable && (
          <button
            type="button"
            className="button button--small"
            aria-label={`עריכת בניין: ${building.name}`}
            onClick={() => {
              setEditing((v) => !v);
            }}
          >
            עריכה
          </button>
        )}
      </div>
      {editing && (
        <form
          className="building__edit"
          onSubmit={(e) => {
            e.preventDefault();
            void save('active');
          }}
        >
          <label className="field">
            <span>קוד</span>
            <input
              value={values.code}
              maxLength={12}
              disabled={building.units > 0}
              onChange={(e) => {
                setValues((v) => ({ ...v, code: e.target.value }));
              }}
            />
          </label>
          <label className="field">
            <span>שם</span>
            <input
              value={values.name}
              maxLength={80}
              onChange={(e) => {
                setValues((v) => ({ ...v, name: e.target.value }));
              }}
            />
          </label>
          <label className="field">
            <span>סוג</span>
            <select
              value={values.kind}
              onChange={(e) => {
                setValues((v) => ({ ...v, kind: e.target.value as StructureKind }));
              }}
            >
              {STRUCTURE_KINDS.map((k) => (
                <option key={k} value={k}>
                  {STRUCTURE_KIND_LABELS[k]}
                </option>
              ))}
            </select>
          </label>
          <div className="form-actions">
            <button type="submit" className="button button--primary">
              שמירה
            </button>
            {building.status === 'active' && (
              <button
                type="button"
                className="button button--danger"
                disabled={building.units > 0}
                title={building.units > 0 ? 'לבניין יש יחידות' : undefined}
                onClick={() => {
                  void askConfirm({
                    title: `להעביר את ${building.name} לארכיון?`,
                    message: 'הבניין לא יוצג יותר ברשימות. אפשר להחזיר אותו מהארכיון.',
                    confirmLabel: 'העברה לארכיון',
                    danger: true,
                  }).then((yes) => {
                    if (yes) void save('archived');
                  });
                }}
              >
                העברה לארכיון
              </button>
            )}
          </div>
          {building.units > 0 && (
            <p className="hint">לבניין יש יחידות: הקוד שלו קבוע ולא ניתן להעבירו לארכיון.</p>
          )}
        </form>
      )}
      <ul className="floor-list" aria-label={`קומות: ${building.name}`}>
        {building.levels.map((l) => (
          <li key={l.id}>
            <button
              type="button"
              className="floor"
              disabled={!editable}
              aria-label={`קומה ${l.label}${l.units > 0 ? `, ${String(l.units)} יחידות` : ''}`}
              onClick={() => {
                setLevel({
                  id: l.id,
                  number: String(l.number),
                  label: l.label,
                  rowVersion: l.rowVersion,
                });
              }}
            >
              {l.label}
              {l.units > 0 && <span className="floor__units">{l.units}</span>}
            </button>
          </li>
        ))}
      </ul>
      {editable && building.status === 'active' && (
        <form
          className="floor-add"
          onSubmit={(e) => {
            e.preventDefault();
            const from = Number(range.from);
            const to = range.to.trim() === '' ? from : Number(range.to);
            if (!Number.isInteger(from) || !Number.isInteger(to) || to < from) {
              setNotice({ kind: 'error', text: 'יש להזין טווח קומות תקין (למשל ‎-2 עד 8).' });
              return;
            }
            void run(
              () =>
                apiPost(
                  `${base}/structures/${building.id}/levels`,
                  { from, to },
                  adminStructuresResponseSchema,
                ),
              'הקומות נוספו.',
            );
          }}
        >
          <label className="field">
            <span>מקומה</span>
            <input
              type="number"
              inputMode="numeric"
              dir="ltr"
              value={range.from}
              onChange={(e) => {
                setRange((r) => ({ ...r, from: e.target.value }));
              }}
            />
          </label>
          <label className="field">
            <span>עד קומה</span>
            <input
              type="number"
              inputMode="numeric"
              dir="ltr"
              value={range.to}
              onChange={(e) => {
                setRange((r) => ({ ...r, to: e.target.value }));
              }}
            />
          </label>
          <button type="submit" className="button">
            + הוספת קומות
          </button>
        </form>
      )}
      <NoticeLine notice={notice} />
      <Modal
        title="עריכת קומה"
        open={level !== null}
        onClose={() => {
          setLevel(null);
        }}
      >
        {level !== null && (
          <form
            className="project-form"
            onSubmit={(e) => {
              e.preventDefault();
              const current = level;
              setLevel(null);
              void run(
                () =>
                  apiPut(
                    `${base}/levels/${current.id}`,
                    {
                      rowVersion: current.rowVersion,
                      number: Number(current.number),
                      label: current.label.trim(),
                    },
                    adminStructuresResponseSchema,
                  ),
                'הקומה עודכנה.',
              );
            }}
          >
            <label className="field">
              <span>מספר קומה</span>
              <input
                type="number"
                dir="ltr"
                value={level.number}
                onChange={(e) => {
                  setLevel({ ...level, number: e.target.value });
                }}
              />
            </label>
            <label className="field">
              <span>שם הקומה</span>
              <input
                value={level.label}
                maxLength={40}
                onChange={(e) => {
                  setLevel({ ...level, label: e.target.value });
                }}
              />
            </label>
            <p className="hint">בקומה עם יחידות אפשר לשנות רק את השם.</p>
            <div className="form-actions">
              <button type="submit" className="button button--primary">
                שמירה
              </button>
            </div>
          </form>
        )}
      </Modal>
    </li>
  );
}

/** Buildings and floors of a project (also opened from "מבנה ודירות" on a phone). */
export function Structures({
  projectId,
  onChanged,
}: {
  readonly projectId: string;
  readonly onChanged: () => void;
}) {
  const [version, setVersion] = useState(0);
  const [data, reload] = useLoad(`admin-structures:${projectId}:${String(version)}`, () =>
    apiGet(`/project-admin/${projectId}/structures`, adminStructuresResponseSchema),
  );
  const [adding, setAdding] = useState({
    code: '',
    name: '',
    kind: 'residential' as StructureKind,
  });
  const [notice, setNotice] = useState<Notice>(null);
  if (data.state === 'loading') return <LoadingBlock />;
  if (data.state === 'forbidden')
    return <Problem text="אין לך הרשאה לצפות בבניינים של פרויקט זה." />;
  if (data.state === 'error')
    return <Problem text="טעינת הבניינים נכשלה." requestId={data.requestId} onRetry={reload} />;
  const { items, permissions } = data.data;
  const changed = (_: AdminStructuresResponse, text: string) => {
    setNotice({ kind: 'done', text });
    setVersion((v) => v + 1);
    onChanged();
  };
  return (
    <section className="structures" aria-label="בניינים וקומות">
      <NoticeLine notice={notice} />
      {items.length === 0 ? (
        <p className="empty">אין עדיין בניינים בפרויקט.</p>
      ) : (
        <ul className="building-list">
          {items.map((b) => (
            <BuildingCard
              key={`${b.id}:${String(b.rowVersion)}:${String(b.levels.length)}`}
              projectId={projectId}
              building={b}
              editable={permissions.edit}
              onChanged={changed}
            />
          ))}
        </ul>
      )}
      {permissions.edit && (
        <form
          className="building-add"
          onSubmit={(e) => {
            e.preventDefault();
            if (adding.code.trim() === '' || adding.name.trim() === '') return;
            setNotice(null);
            apiPost(
              `/project-admin/${projectId}/structures`,
              { code: adding.code.trim(), name: adding.name.trim(), kind: adding.kind },
              adminStructuresResponseSchema,
            ).then(
              (result) => {
                setAdding({ code: '', name: '', kind: 'residential' });
                changed(result, 'הבניין נוסף. כעת הוסיפו לו קומות.');
              },
              (error: unknown) => {
                setNotice({ kind: 'error', text: explainProjectError(error) });
              },
            );
          }}
        >
          <label className="field">
            <span>קוד בניין</span>
            <input
              value={adding.code}
              maxLength={12}
              placeholder="למשל A"
              onChange={(e) => {
                setAdding((a) => ({ ...a, code: e.target.value }));
              }}
            />
          </label>
          <label className="field">
            <span>שם הבניין</span>
            <input
              value={adding.name}
              maxLength={80}
              placeholder="למשל בניין A"
              onChange={(e) => {
                setAdding((a) => ({ ...a, name: e.target.value }));
              }}
            />
          </label>
          <label className="field">
            <span>סוג</span>
            <select
              value={adding.kind}
              onChange={(e) => {
                setAdding((a) => ({ ...a, kind: e.target.value as StructureKind }));
              }}
            >
              {STRUCTURE_KINDS.map((k) => (
                <option key={k} value={k}>
                  {STRUCTURE_KIND_LABELS[k]}
                </option>
              ))}
            </select>
          </label>
          <button
            type="submit"
            className="button button--primary"
            disabled={adding.code.trim() === '' || adding.name.trim() === ''}
          >
            + הוספת בניין
          </button>
        </form>
      )}
    </section>
  );
}

function ProjectSetupView({
  project,
  onChanged,
  onBack,
  onOpenSheet,
}: {
  readonly project: ProjectAdminItem;
  readonly onChanged: () => void;
  readonly onBack: () => void;
  readonly onOpenSheet: (projectId: string) => void;
}) {
  const steps = setupSteps(project.setup);
  const [step, setStep] = useState<SetupStepId | 'advanced'>(
    steps.find((s) => !s.done)?.id ?? 'details',
  );
  const [advancedTab, setAdvancedTab] = useState<
    'contractor' | 'template' | 'general' | 'acknowledgment'
  >('contractor');
  const id = useId();
  const done = steps.filter((s) => s.done).length;
  const forHandover = readinessChecks(project.setup).filter((c) => !c.done && !c.optional);
  const advanced = (tab: typeof advancedTab) => {
    setAdvancedTab(tab);
    setStep('advanced');
  };
  return (
    <section className="project-setup" aria-labelledby={`${id}-title`}>
      <button type="button" className="button button--ghost back-button" onClick={onBack}>
        → כל הפרויקטים
      </button>
      <div className="project-setup__head">
        <h2 id={`${id}-title`} className="section-title">
          {project.name}{' '}
          <span className="hint">
            (<bdi dir="ltr">{project.code}</bdi>)
          </span>
        </h2>
        <span className={`chip chip--project-${project.status}`}>
          {PROJECT_STATUS_LABELS[project.status]}
        </span>
      </div>
      <ProgressBar
        progress={{ total: steps.length, checked: done, defects: 0 }}
        label="התקדמות הקמת הפרויקט"
      />
      <p className="project-setup__summary">
        הושלמו {done} מתוך {steps.length} שלבי הקמה
      </p>
      <p
        className={`project-setup__readiness project-setup__readiness--${forHandover.length === 0 ? 'ready' : 'missing'}`}
      >
        {forHandover.length === 0
          ? 'הפרויקט מוכן למסירה הראשונה.'
          : `לפני המסירה הראשונה חסרים: ${forHandover.map((c) => c.label).join(', ')}.`}
      </p>
      <nav aria-label="שלבי הקמה" className="setup-steps">
        <ol>
          {steps.map((s, index) => (
            <li key={s.id}>
              <button
                type="button"
                className={`setup-step${s.done ? ' setup-step--done' : ''}`}
                aria-current={s.id === step ? 'step' : undefined}
                onClick={() => {
                  setStep(s.id);
                }}
              >
                <span className="setup-step__mark" aria-hidden="true">
                  {s.done ? '✓' : String(index + 1)}
                </span>
                <span>{s.label}</span>
                <span className="visually-hidden">{s.done ? ' – הושלם' : ' – טרם הושלם'}</span>
              </button>
              {!s.done && s.required && (
                <span className="setup-step__missing">{s.missing} נדרש להפעלת הפרויקט.</span>
              )}
            </li>
          ))}
        </ol>
      </nav>
      <div className="setup-panel">
        {step === 'details' &&
          (project.permissions.edit ? (
            <DetailsForm
              key={project.id}
              creating={false}
              submitLabel="שמירת פרטי הפרויקט"
              initial={{
                name: project.name,
                code: project.code,
                city: project.city,
                address: project.address ?? '',
                description: project.description ?? '',
                status: project.status,
              }}
              onSubmit={async (values) => {
                await apiPut(
                  `/project-admin/${project.id}`,
                  {
                    rowVersion: project.rowVersion,
                    name: values.name.trim(),
                    code: values.code.trim(),
                    city: values.city.trim(),
                    address: values.address,
                    description: values.description,
                    status: values.status,
                  },
                  projectAdminItemSchema,
                );
                onChanged();
              }}
            />
          ) : (
            <p className="notice">אין לך הרשאה לערוך את פרטי הפרויקט.</p>
          ))}
        {step === 'structures' && (
          <div className="setup-structures">
            <Structures projectId={project.id} onChanged={onChanged} />
            <div className="setup-apartments">
              <h3>דירות</h3>
              <p>
                {project.setup.apartments === 0
                  ? 'לפרויקט אין עדיין דירות. מוסיפים אותן בגיליון הדירות (הקלדה או הדבקה מ-Excel).'
                  : `בפרויקט ${String(project.setup.apartments)} דירות.`}
              </p>
              <button
                type="button"
                className="button button--primary"
                onClick={() => {
                  onOpenSheet(project.id);
                }}
              >
                פתיחת גיליון הדירות של הפרויקט
              </button>
            </div>
          </div>
        )}
        {step === 'team' && <ProjectTeam projectId={project.id} />}
        {step === 'readiness' && (
          <ReadinessStep
            project={project}
            onChanged={onChanged}
            onStep={(target) => {
              setStep(target);
            }}
            onAdvanced={advanced}
          />
        )}
        {step === 'advanced' && (
          <HandoverSettings key={advancedTab} projectId={project.id} initialTab={advancedTab} />
        )}
      </div>
      <div className="setup-advanced">
        <button
          type="button"
          className="button button--ghost"
          aria-pressed={step === 'advanced'}
          onClick={() => {
            advanced('contractor');
          }}
        >
          <Icon name="settings" /> הגדרות מתקדמות (קבלנים, תחומים, חדרים, התאמת רשימת הבדיקה)
        </button>
      </div>
    </section>
  );
}

/** "רשימת הבדיקה": the company checklist the project follows, applied in one action (DR-195). */
/** "בדיקת מוכנות למסירות": everything a first handover needs, each with the way to fix it. */
function ReadinessStep({
  project,
  onChanged,
  onStep,
  onAdvanced,
}: {
  readonly project: ProjectAdminItem;
  readonly onChanged: () => void;
  readonly onStep: (step: SetupStepId) => void;
  readonly onAdvanced: (tab: 'contractor' | 'template' | 'general' | 'acknowledgment') => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const checks = readinessChecks(project.setup);
  // Normally automatic (publishing applies the company checklist to every project); this only
  // repairs a project that missed it.
  const applyMaster = () => {
    setBusy(true);
    setError(null);
    apiPost(
      `/projects/${project.id}/inspection-template/master`,
      {},
      inspectionTemplateResponseSchema,
    )
      .then(onChanged)
      .catch((e: unknown) => {
        setError(explainProjectError(e));
      })
      .finally(() => {
        setBusy(false);
      });
  };
  const action = (c: (typeof checks)[number]): [string, () => void] | null => {
    switch (c.id) {
      case 'structures':
      case 'apartments':
        return [
          'להוספה',
          () => {
            onStep('structures');
          },
        ];
      case 'team':
        return [
          'לשיוך עובדים',
          () => {
            onStep('team');
          },
        ];
      case 'checklist':
        return project.setup.masterLatest === null ? null : ['החלה על הפרויקט', applyMaster];
      case 'contractors':
        return [
          'להגדרת קבלנים',
          () => {
            onAdvanced('contractor');
          },
        ];
      case 'wording':
        return [
          'לנוסח ההצהרה',
          () => {
            onAdvanced('acknowledgment');
          },
        ];
      case 'general':
        return [
          'לבדיקות הכלליות',
          () => {
            onAdvanced('general');
          },
        ];
    }
  };
  return (
    <>
      {error !== null && (
        <p className="notice notice--error" role="alert">
          {error}
        </p>
      )}
      <ul className="readiness-list" aria-label="מה צריך לפני המסירה הראשונה">
        {checks.map((c) => {
          const run = action(c);
          return (
            <li
              key={c.id}
              className={`readiness-item readiness-item--${c.done ? 'done' : c.optional ? 'optional' : 'missing'}`}
            >
              <Icon name={c.done ? 'check' : 'alert'} />
              <span className="readiness-item__text">
                <b>{c.label}</b>
                <span className="hint">
                  {c.done ? 'מוכן' : c.optional ? `רשות · ${c.hint}` : c.hint}
                </span>
              </span>
              {!c.done && run !== null && (
                <button type="button" className="button" disabled={busy} onClick={run[1]}>
                  {run[0]}
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </>
  );
}

/** ניהול פרויקטים. */
export function ProjectAdmin({
  onWorkspaceChanged,
  onOpenSheet,
}: {
  /** The project lists elsewhere change (a new or renamed project). */
  readonly onWorkspaceChanged: () => void;
  readonly onOpenSheet: (projectId: string) => void;
}) {
  const [version, setVersion] = useState(0);
  const [data, reload] = useLoad(`project-admin:${String(version)}`, () =>
    apiGet('/project-admin', projectAdminListResponseSchema),
  );
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const titleId = useId();
  const changed = () => {
    setVersion((v) => v + 1);
    onWorkspaceChanged();
  };
  const [last, setLast] = useState<typeof data | null>(null);
  if (data.state === 'ready' && last !== data) setLast(data);
  const shown = data.state === 'loading' && last?.state === 'ready' ? last : data;
  if (shown.state === 'loading') return <LoadingBlock />;
  if (shown.state === 'forbidden')
    return <Problem text="ניהול פרויקטים זמין למנהלי פרויקטים ולמנהלי הארגון בלבד." />;
  if (shown.state === 'error')
    return <Problem text="טעינת הפרויקטים נכשלה." requestId={shown.requestId} onRetry={reload} />;
  const { items, permissions } = shown.data;
  const open = items.find((p) => p.id === openId);
  if (open !== undefined)
    return (
      <ProjectSetupView
        key={open.id}
        project={open}
        onChanged={changed}
        onBack={() => {
          setOpenId(null);
        }}
        onOpenSheet={onOpenSheet}
      />
    );
  return (
    <section className="project-admin" aria-labelledby={titleId}>
      <div className="project-admin__head">
        <h2 id={titleId} className="section-title">
          ניהול פרויקטים <span className="count">({items.length})</span>
        </h2>
        {permissions.create && (
          <button
            type="button"
            className="button button--primary"
            onClick={() => {
              setCreating(true);
            }}
          >
            + פרויקט חדש
          </button>
        )}
      </div>
      {items.length === 0 ? (
        <p className="empty">אין פרויקטים לניהול.</p>
      ) : (
        <ul className="project-cards">
          {items.map((p) => {
            const steps = setupSteps(p.setup);
            const done = steps.filter((s) => s.done).length;
            return (
              <li key={p.id} className="project-card" aria-label={p.name}>
                <div className="project-card__head">
                  <p className="project-card__name">{p.name}</p>
                  <span className="hint">
                    {p.code} · {p.city}
                  </span>
                  <span className={`chip chip--project-${p.status}`}>
                    {PROJECT_STATUS_LABELS[p.status]}
                  </span>
                </div>
                <ProgressBar
                  progress={{ total: steps.length, checked: done, defects: 0 }}
                  label={`הקמת ${p.name}`}
                />
                <p className="hint">
                  הקמה: {done} מתוך {steps.length} · {p.setup.buildings} בניינים ·{' '}
                  {p.setup.apartments} דירות · {p.setup.members} בצוות
                </p>
                <button
                  type="button"
                  className="button"
                  aria-label={`הגדרת הפרויקט: ${p.name}`}
                  onClick={() => {
                    setOpenId(p.id);
                  }}
                >
                  הגדרת הפרויקט
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <Modal
        title="פרויקט חדש"
        open={creating}
        onClose={() => {
          setCreating(false);
        }}
      >
        {creating && (
          <DetailsForm
            creating
            submitLabel="יצירת הפרויקט"
            initial={{
              name: '',
              code: '',
              city: '',
              address: '',
              description: '',
              status: 'setup',
            }}
            onCancel={() => {
              setCreating(false);
            }}
            onSubmit={async (values) => {
              const created = await apiPost(
                '/project-admin',
                {
                  name: values.name.trim(),
                  code: values.code.trim(),
                  city: values.city.trim(),
                  address: values.address,
                  description: values.description,
                },
                projectAdminItemSchema,
              );
              setCreating(false);
              changed();
              setOpenId(created.id);
            }}
          />
        )}
      </Modal>
    </section>
  );
}
